/**
 * Notification Store - Toast/Alert System
 */
import { create } from 'zustand';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
}

interface NotificationState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

let notificationId = 0;

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  addNotification: (notification) => {
    const id = `notification-${++notificationId}`;
    const duration = notification.duration ?? 5000;

    set((state) => ({
      notifications: [...state.notifications, { ...notification, id }],
    }));

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      }, duration);
    }
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAll: () => {
    set({ notifications: [] });
  },
}));

// Convenience functions for use throughout the app
export const notify = {
  success: (title: string, message?: string, duration?: number) => {
    useNotificationStore.getState().addNotification({ type: 'success', title, message, duration });
  },
  error: (title: string, message?: string, duration?: number) => {
    useNotificationStore
      .getState()
      .addNotification({ type: 'error', title, message, duration: duration ?? 8000 });
  },
  warning: (title: string, message?: string, duration?: number) => {
    useNotificationStore.getState().addNotification({ type: 'warning', title, message, duration });
  },
  info: (title: string, message?: string, duration?: number) => {
    useNotificationStore.getState().addNotification({ type: 'info', title, message, duration });
  },
};
